"use client";

import React from 'react';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';

export function StandaloneThemeToggle() {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);
  const [isAnimating, setIsAnimating] = React.useState(false);

  // Handle mounting to avoid hydration mismatch
  React.useEffect(() => {
    setMounted(true);
  }, []);

  // Add custom keyframe animations
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes theme-pulse {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.2); opacity: 0.8; }
      }

      @keyframes sun-rise {
        0% { transform: translateY(10px) rotate(-90deg) scale(0); opacity: 0; }
        50% { transform: translateY(-5px) rotate(0deg) scale(1.1); opacity: 1; }
        100% { transform: translateY(0) rotate(90deg) scale(1); opacity: 1; }
      }

      @keyframes moon-phase {
        0% { transform: rotate(0deg) scale(0); opacity: 0; }
        50% { transform: rotate(180deg) scale(1.1); opacity: 1; }
        100% { transform: rotate(360deg) scale(1); opacity: 1; }
      }

      @keyframes ray-burst {
        0% { transform: scale(0) rotate(0deg); opacity: 0; }
        50% { transform: scale(1.5) rotate(180deg); opacity: 1; }
        100% { transform: scale(1) rotate(360deg); opacity: 0; }
      }

      .theme-transition-sun {
        animation: sun-rise 600ms cubic-bezier(0.34, 1.56, 0.64, 1);
      }

      .theme-transition-moon {
        animation: moon-phase 600ms cubic-bezier(0.34, 1.56, 0.64, 1);
      }

      .theme-pulse {
        animation: theme-pulse 600ms ease-in-out;
      }

      .ray-burst {
        animation: ray-burst 800ms ease-out;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Don't render anything until mounted to avoid hydration mismatch
  if (!mounted) {
    return (
      <div className="w-10 h-10 rounded-full bg-background border border-border flex items-center justify-center">
        <Sun className="h-4 w-4 text-muted-foreground" />
      </div>
    );
  }

  const isDark = theme === 'dark';

  const handleThemeToggle = () => {
    setIsAnimating(true);
    setTheme(isDark ? 'light' : 'dark');
    setTimeout(() => setIsAnimating(false), 600);
  };

  return (
    <button
      onClick={handleThemeToggle}
      className={`group relative w-10 h-10 rounded-full bg-background border border-border flex items-center justify-center transition-all duration-500 hover:border-border-strong hover:scale-110 hover:rotate-12 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background cursor-pointer overflow-hidden ${
        isAnimating ? 'theme-pulse' : ''
      }`}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      {/* Animated background gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br transition-colors duration-700 ${
        isDark
          ? 'from-muted/10 via-accent/10 to-background/10'
          : 'from-amber-100/20 via-orange-100/20 to-yellow-100/20'
      }`} />

      {/* Burst effect on click */}
      {isAnimating && (
        <div className="absolute inset-0 ray-burst">
          <div className="absolute inset-2 rounded-full bg-gradient-to-br from-primary/20 via-accent/20 to-transparent" />
        </div>
      )}

      {/* Icon container */}
      <div className="relative z-10">
        {isDark ? (
          <Moon className={`h-4 w-4 text-primary transition-all duration-500 group-hover:text-primary/80 group-hover:scale-125 filter group-hover:drop-shadow-sm ${
            isAnimating ? 'theme-transition-moon' : ''
          }`} />
        ) : (
          <Sun className={`h-4 w-4 text-amber-500 transition-all duration-500 group-hover:text-amber-400 group-hover:scale-125 filter group-hover:drop-shadow-sm ${
            isAnimating ? 'theme-transition-sun' : ''
          }`} />
        )}
      </div>

      {/* Animated rays for sun - rotating effect */}
      {!isDark && (
        <div className={`absolute inset-0 transition-all duration-500 ${
          isAnimating ? 'animate-spin' : ''
        }`}>
          <div className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-500">
            <div className="absolute top-0 left-1/2 w-0.5 h-3 bg-gradient-to-b from-amber-400 to-transparent transform -translate-x-1/2" />
            <div className="absolute bottom-0 left-1/2 w-0.5 h-3 bg-gradient-to-t from-amber-400 to-transparent transform -translate-x-1/2" />
            <div className="absolute left-0 top-1/2 w-3 h-0.5 bg-gradient-to-r from-amber-400 to-transparent transform -translate-y-1/2" />
            <div className="absolute right-0 top-1/2 w-3 h-0.5 bg-gradient-to-l from-amber-400 to-transparent transform -translate-y-1/2" />
            {/* Diagonal rays */}
            <div className="absolute top-1 left-1 w-2 h-0.5 bg-gradient-to-br from-amber-300 to-transparent transform rotate-45 origin-right" />
            <div className="absolute top-1 right-1 w-2 h-0.5 bg-gradient-to-bl from-amber-300 to-transparent transform -rotate-45 origin-left" />
            <div className="absolute bottom-1 left-1 w-2 h-0.5 bg-gradient-to-tr from-amber-300 to-transparent transform -rotate-45 origin-right" />
            <div className="absolute bottom-1 right-1 w-2 h-0.5 bg-gradient-to-tl from-amber-300 to-transparent transform rotate-45 origin-left" />
          </div>
        </div>
      )}

      {/* Animated stars for moon - twinkling effect */}
      {isDark && (
        <div className="absolute inset-0">
          <div className={`absolute inset-0 transition-all duration-700 ${
            isAnimating ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'
          }`}>
            <div className="absolute top-1 right-2 w-1 h-1 bg-primary/40 rounded-full animate-pulse"
                 style={{animationDuration: '1s', animationDelay: '0s'}} />
            <div className="absolute bottom-2 left-1 w-0.5 h-0.5 bg-primary/30 rounded-full animate-pulse"
                 style={{animationDuration: '1.5s', animationDelay: '0.3s'}} />
            <div className="absolute top-2 left-2 w-0.5 h-0.5 bg-accent-foreground/40 rounded-full animate-pulse"
                 style={{animationDuration: '2s', animationDelay: '0.6s'}} />
            <div className="absolute bottom-1 right-2 w-1 h-1 bg-muted-foreground/40 rounded-full animate-pulse"
                 style={{animationDuration: '1.2s', animationDelay: '0.9s'}} />
            {/* Extra stars that appear during animation */}
            {isAnimating && (
              <>
                <div className="absolute top-1/2 right-1 w-0.5 h-0.5 bg-primary/60 rounded-full animate-ping" />
                <div className="absolute top-3 left-1/2 w-0.5 h-0.5 bg-accent-foreground/60 rounded-full animate-ping"
                     style={{animationDelay: '0.2s'}} />
                <div className="absolute left-1 bottom-1/2 w-0.5 h-0.5 bg-muted-foreground/60 rounded-full animate-ping"
                     style={{animationDelay: '0.4s'}} />
              </>
            )}
          </div>
        </div>
      )}

      {/* Enhanced hover glow with animation */}
      <div className={`absolute inset-0 rounded-full transition-all duration-500 group-hover:scale-110 ${
        isDark
          ? 'bg-gradient-to-br from-transparent via-primary/5 to-accent/10'
          : 'bg-gradient-to-br from-transparent via-amber-500/5 to-orange-500/10'
      } opacity-0 group-hover:opacity-100`} />
    </button>
  );
}
